import React from 'react';
import { gql } from '@soundxyz/gql-string';
import { NULL_ADDRESS } from '@soundxyz/utils/constants/constants';
import {
  useBuyInfo,
  useSamBuyQuantityReducer,
} from '@components/Song/MintComponents/MintComponent/BondingCurve/CollectInfo';
import { NetworkIndicator } from '@components/Song/MintComponents/NetworkIndicator';
import { QuantityInput } from '@components/main-components/inputs/QuantityInput';
import { StandardModal } from '@components/main-components/modals/StandardModal';
import { ModalContext } from '@context/modal';
import type { FragmentType } from '@graphql/reactQuery';
import {
  CollectSoundSwapEthConversionDocument,
  getFragment,
  SamCollectInfoFragmentDoc,
  useQuery,
} from '@graphql/reactQuery';
import { formatWeiToEthDisplayFree, formatWeiToEthForDisplay } from '@utils/helpers';
import type { PlaylistReferral } from '@utils/types';
import { PaySamWithCreditCard } from '../CreditCard/PayWithCCButton';
import { PaySamWithEth } from '../L2CheckoutWithEth/CheckoutWithEth';
import { PayWithETHButton } from '../L2CheckoutWithEth/PayWithEthButton';
import { ReleaseDetails } from '../shared';

/*#__PURE__*/ gql(/* GraphQL */ `
  query CollectSoundSwapETHConversion {
    currencies {
      ethToUsd
    }
  }
`);

export function BuySamEditionModal({
  disableCollecting,
  data,
  maxMintableByUser,
  setHasError,
  playlistReferral,
}: {
  disableCollecting: boolean;
  data: FragmentType<SamCollectInfoFragmentDoc>;
  maxMintableByUser: number;
  setHasError: React.Dispatch<React.SetStateAction<boolean>>;
  playlistReferral: PlaylistReferral | null;
}) {
  const { closeCancelModal } = ModalContext.useContainer();
  const release = getFragment(SamCollectInfoFragmentDoc, data);
  const { state, dispatch } = useSamBuyQuantityReducer({ releaseId: release.id });
  const selectedQuantity = state.value === '' ? 0 : state.value;
  const { isBuying, handleBuy, valueIncludingTransactionFee, getAffiliate } = useBuyInfo({
    data,
    selectedQuantity,
    playlistReferral,
  });
  const { data: ethToUsdData } = useQuery(CollectSoundSwapEthConversionDocument, {
    filterQueryKey: {
      key: 'EthToUsd',
    },
    staleTime: 0,
  });

  const ethToUsdValue = ethToUsdData?.data.currencies.ethToUsd || 0;

  const totalPriceWei = valueIncludingTransactionFee.toString();
  const totalPriceEth = formatWeiToEthForDisplay({ weiValue: totalPriceWei });
  const totalPriceEthString = formatWeiToEthDisplayFree({
    weiValue: totalPriceWei,
    maxDecimals: 5,
  });
  const totalUsdPrice = (ethToUsdValue * Number(totalPriceEth)).toFixed(2);

  const body = (
    <div className="-mb-3 flex flex-col gap-4">
      <ReleaseDetails releaseData={release} selectedMintPanel="LIMITED" />
      <div className="flex items-center justify-between rounded-md bg-base100 p-3 ring-[3px]  ring-base900 md:p-4">
        <div className="flex flex-col gap-1.5 md:gap-1">
          <h3 className="font-title text-title-s text-base900 md:text-title-m">Sound Swap</h3>
          <p className="font-base text-base-s font-semibold text-base900">{totalPriceEthString}</p>
        </div>
        <QuantityInput
          state={state}
          min={1}
          dispatch={dispatch}
          max={maxMintableByUser}
          onError={error => {
            switch (error) {
              case 'SET':
                setHasError(true);
                break;
              default:
                setHasError(false);
            }
          }}
        />
      </div>
      <div className="flex w-full flex-col gap-2">
        <div className="flex w-full justify-between">
          <h2 className="font-title text-title-m font-medium">Total</h2>
          <div className="flex flex-col items-end">
            <div className="flex items-center gap-1">
              <NetworkIndicator chainId={release.chainId} size="sm" />
              <h2 className="font-title text-title-m font-medium">{totalPriceEthString}</h2>
            </div>
            {valueIncludingTransactionFee !== 0n && (
              <p className="text-right text-base-m text-base400">~${totalUsdPrice}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
  const affiliate = getAffiliate({ playlistReferral }) || NULL_ADDRESS;

  return (
    <StandardModal
      body={body}
      showHeaderShadow={false}
      title="Checkout"
      callToAction={
        <div className="-mt-1 flex w-full flex-col gap-y-3">
          <PayWithETHButton
            onBuy={handleBuy}
            totalPriceWei={totalPriceWei}
            chainId={release.chainId}
            disableCollecting={disableCollecting}
            isBuying={isBuying}
            payWithEthButton={
              <PaySamWithEth
                source="Insufficient Funds Modal"
                chainId={release.chainId}
                quantity={state.value || 0}
                affiliate={affiliate}
                disabled={disableCollecting}
                data={release}
                releaseId={release.id}
                minterAddress={release.samAddress || ''}
                buyPriceWei={totalPriceWei}
                className="justify-between"
                variant="fuchsia"
                playlistReferral={playlistReferral}
              />
            }
            payWithCardButton={
              <PaySamWithCreditCard
                contractAddress={release.contractAddress}
                source="Insufficient Funds Modal"
                quantity={state.value || 0}
                minterAddress={release.samAddress || ''}
                affiliate={affiliate || NULL_ADDRESS}
                disabled={disableCollecting}
                data={release}
                releaseId={release.id}
                buyPriceWei={totalPriceWei}
                className="justify-between"
                variant="fuchsia"
                chainId={release.chainId}
                playlistReferral={playlistReferral}
              />
            }
          />
          <PaySamWithCreditCard
            contractAddress={release.contractAddress}
            source="Collect Modal"
            variant="tertiary"
            quantity={state.value || 0}
            minterAddress={release.samAddress || ''}
            affiliate={affiliate || NULL_ADDRESS}
            disabled={disableCollecting}
            data={release}
            releaseId={release.id}
            buyPriceWei={totalPriceWei}
            className="justify-between"
            chainId={release.chainId}
            playlistReferral={playlistReferral}
          />
        </div>
      }
      bodyWithoutPadding
      closeModal={closeCancelModal}
    />
  );
}
